import { CommonModule } from '@angular/common';
import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AbntPipe } from '../../pipes/abnt.pipe';
import { AlertService, AlertType } from '../../services/alert.service';
import { OrdemProducaoOrquestradorService } from '../../services/apis/orquestrador-suprimentos/ordem-producao-orquestrador.service';
import { EstruturaProdutoLinhaStruct } from '../../services/structs/suprimentos/estrutura-produto-linha.struct';
import { EmpresaEtiquetaDadosService } from '../../services/etiqueta/empresa-etiqueta-dados.service';

export interface ImpressaoEstruturaProdutoOrdemProducaoDialogData {
  idOrdemProducao: number;
}

/**
 * Impressão "Estrutura do Produto" (seção 7.2 da US#141200) — explosão recursiva multinível da
 * composição, já achatada em pré-ordem pelo Orquestrador (GET .../estrutura). Só indenta por
 * `linha.nivel`, sem montar árvore no front.
 */
@Component({
  selector: 'app-impressao-estrutura-produto-ordem-producao-dialog',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule, MatProgressSpinnerModule, AbntPipe],
  templateUrl: './impressao-estrutura-produto-ordem-producao-dialog.component.html',
  styleUrl: './impressao-estrutura-produto-ordem-producao-dialog.component.css',
})
export class ImpressaoEstruturaProdutoOrdemProducaoDialogComponent implements OnInit {
  public isLoading = false;

  public numero = 0;
  public nomeProduto = '';
  public codigoSku = '';
  public linhas: EstruturaProdutoLinhaStruct[] = [];

  public empresaNome = '';
  public empresaCnpj = '';
  public empresaEndereco = '';
  public empresaLogo = '';

  constructor(
    public dialogRef: MatDialogRef<ImpressaoEstruturaProdutoOrdemProducaoDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: ImpressaoEstruturaProdutoOrdemProducaoDialogData,
    private ordemProducaoOrquestradorService: OrdemProducaoOrquestradorService,
    private empresaEtiquetaDadosService: EmpresaEtiquetaDadosService,
    private alertService: AlertService,
  ) { }

  public ngOnInit(): void {
    this.carregarDadosEmpresa();
    this.buscarDadosEstrutura();
  }

  public buscarDadosEstrutura() {
    this.isLoading = true;
    this.ordemProducaoOrquestradorService.getEstrutura(this.data.idOrdemProducao).subscribe({
      next: (res) => {
        this.isLoading = false;
        if (res.isError) {
          this.alertService.show('Erro', res.errorDescription, AlertType.error);
          this.fechar();
          return;
        }
        this.numero = res.numero;
        this.nomeProduto = res.nomeProduto;
        this.codigoSku = res.codigoSku;
        this.linhas = res.linhas;
      },
      error: () => {
        this.isLoading = false;
        this.alertService.show('Erro', 'Não foi possível carregar a estrutura do produto.', AlertType.error);
        this.fechar();
      },
    });
  }


  /** Mesmo mecanismo usado por ImpressaoProdutoListaSimplesComponent e os demais `impressao-*`. */
  public imprimir(): void {
    const conteudoParaImprimir = document.querySelector('.modal-print-container');
    if (!conteudoParaImprimir) return;

    const janelaImpressao = window.open('', '', 'height=600,width=800');
    if (!janelaImpressao) return;

    const styles = Array.from(document.styleSheets)
      .map((styleSheet) => {
        try {
          return Array.from(styleSheet.cssRules).map((rule) => rule.cssText).join('');
        } catch {
          return '';
        }
      })
      .join('');

    janelaImpressao.document.write(`
      <html>
        <head>
          <title>Estrutura do Produto - OP ${this.numero}</title>
          <base href="/">
          <style>
            ${styles}
            html, body {
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
              margin: 20px;
              font-family: Roboto, "Helvetica Neue", sans-serif;
              color: #000;
              background: #fff;
            }
            .no-print { display: none !important; }
            .modal-print-container {
              display: block !important;
              width: 100% !important;
              height: auto !important;
              min-height: 0 !important;
              overflow: visible !important;
              box-shadow: none !important;
              margin: 0 !important;
              padding: 0 !important;
            }
          </style>
        </head>
        <body>
          ${conteudoParaImprimir.innerHTML}
        </body>
      </html>
    `);

    janelaImpressao.document.close();
    janelaImpressao.focus();

    setTimeout(() => {
      janelaImpressao.print();
      janelaImpressao.close();
    }, 800);
  }

  private async carregarDadosEmpresa(): Promise<void> {
    try {
      const valoresEmpresa = await this.empresaEtiquetaDadosService.getValoresEmpresa();
      this.empresaNome = valoresEmpresa['empresa.nome'];
      this.empresaCnpj = valoresEmpresa['empresa.cnpj'];
      this.empresaEndereco = valoresEmpresa['empresa.endereco_completo'];
      this.empresaLogo = valoresEmpresa['empresa.logo'];
    } catch {
      // Sem dados de empresa, o documento imprime só sem o cabeçalho — não é motivo pra travar.
    }
  }

  public fechar(): void {
    this.dialogRef.close();
  }
}
